import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Target, TrendingUp, Shield, Sparkles, Users, User, LogIn, Mail, ArrowLeft, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import LanguageSelector from '../components/LanguageSelector';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { loginWithCredentials, registerUser } from '../lib/api';
import { toast } from 'sonner';

export default function LandingPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated, checkAuth } = useAuth();
  const { t, language } = useLanguage();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginMode, setLoginMode] = useState('select'); // 'select', 'credentials'
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [identifier, setIdentifier] = useState(''); // email or username
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Unified login - accepts email OR username
  const handleLogin = async () => {
    if (!identifier || !password) {
      toast.error(t('fill_all_fields'));
      return;
    }
    
    setIsLoggingIn(true);
    try {
      await loginWithCredentials(identifier.toLowerCase().trim(), password);
      await checkAuth();
      toast.success(t('login_success'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invalid_credentials'));
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleRegister = async () => {
    if (!email || !password || !name) {
      toast.error(t('fill_all_fields'));
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('invalid_email'));
      return;
    }
    
    // Validate password strength
    const passwordErrors = [];
    if (password.length < 8) {
      passwordErrors.push(t('password_8_chars'));
    }
    if (!/[A-Z]/.test(password)) {
      passwordErrors.push(t('password_uppercase'));
    }
    if (!/[a-z]/.test(password)) {
      passwordErrors.push(t('password_lowercase'));
    }
    if (!/[0-9]/.test(password)) {
      passwordErrors.push(t('password_digit'));
    }
    
    if (passwordErrors.length > 0) {
      toast.error(
        t('password_too_weak') + '\n' + 
        passwordErrors.join('\n'),
        { duration: 5000 }
      );
      return;
    }
    
    setIsLoggingIn(true);
    try {
      await registerUser(email.toLowerCase().trim(), password, name);
      toast.success(t('account_created'));
      await loginWithCredentials(email.toLowerCase().trim(), password);
      await checkAuth();
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.errors) {
        toast.error(detail.message + ':\n' + detail.errors.join('\n'), { duration: 5000 });
      } else {
        toast.error(detail || t('registration_error'));
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleGoogleLogin = () => {
    setShowLoginModal(false);
    login();
  };

  const openLoginModal = () => {
    setLoginMode('select');
    setIsRegisterMode(false);
    setIdentifier('');
    setEmail('');
    setName('');
    setPassword('');
    setShowLoginModal(true);
  };

  const resetToSelect = () => {
    setLoginMode('select');
    setIsRegisterMode(false);
    setIdentifier('');
    setEmail('');
    setName('');
    setPassword('');
  };

  const features = [
    { icon: Zap, title: t('fast'), desc: t('fast_desc') },
    { icon: Target, title: t('objectives'), desc: t('reach_goals') },
    { icon: TrendingUp, title: t('insights'), desc: t('understand_habits') },
    { icon: Shield, title: t('secure'), desc: t('encrypted_data') },
    { icon: Sparkles, title: t('ai'), desc: t('personalized_advice') },
    { icon: Users, title: t('family'), desc: t('manage_together') },
  ];

  const stats = [
    { value: '10K+', label: t('active_users') },
    { value: '1 sec', label: t('to_add_expense') },
    { value: '73%', label: t('goals_achieved') },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img 
                src="/spenspot-icon.png" 
                alt="SpenSpot" 
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-bold tracking-tight">SpenSpot</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSelector variant="compact" />
              <Button
                onClick={openLoginModal}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                data-testid="login-btn"
              >
                <LogIn className="w-4 h-4 mr-2 hidden sm:block" />
                {t('hero_login')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 hero-gradient" />
        
        {/* Floating elements */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              {t('hero_main_title_fr')} <span className="text-gradient">{t('hero_main_title_highlight')}</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {t('hero_main_desc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={openLoginModal}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-lg px-8"
                data-testid="cta-btn"
              >
                {t('hero_cta')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">
              {t('everything_you_need')}
            </h2>
            <p className="text-muted-foreground">
              {language === 'fr' 
                ? 'Simple, rapide, intelligent'
                : language === 'pt'
                ? 'Simples, rápido, inteligente'
                : 'Simple, fast, intelligent'
              }
            </p>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card p-6 rounded-2xl text-center hover:scale-105 transition-transform"
              >
                <feature.icon className="w-10 h-10 mx-auto mb-4 text-primary" />
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-3 gap-8 text-center">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="text-3xl sm:text-4xl font-bold text-gradient mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-4">
              {t('ready_to_start')}
            </h2>
            <p className="text-muted-foreground mb-8">
              {t('join_thousands_users')}
            </p>
            <Button
              size="lg"
              onClick={openLoginModal}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-lg px-8"
              data-testid="cta-bottom-btn"
            >
              {t('hero_cta')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <p className="mt-6 text-muted-foreground">
              {t('free_no_commitment')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img 
              src="/spenspot-icon.png" 
              alt="SpenSpot" 
              className="w-8 h-8 rounded-lg"
            />
            <span className="font-bold">SpenSpot</span>
          </div>
          <div className="text-sm text-muted-foreground">
            © 2025 SpenSpot. {t('all_rights_reserved')}
          </div>
        </div>
      </footer>

      {/* Unified Login Modal - 2 Options Only */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="max-w-sm">
          {loginMode === 'select' ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-center">
                  {t('login')}
                </DialogTitle>
                <DialogDescription className="text-center">
                  {language === 'fr' 
                    ? 'Choisissez votre méthode de connexion'
                    : language === 'pt'
                    ? 'Escolha o método de início de sessão'
                    : 'Choose your login method'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-3 py-4">
                {/* Google Login */}
                <button
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-muted hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all group"
                  data-testid="google-login-option"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-100 via-yellow-100 to-blue-100 dark:from-red-900/30 dark:via-yellow-900/30 dark:to-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-semibold">Google</p>
                    <p className="text-sm text-muted-foreground">
                      {t('quick_secure_login')}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
                </button>

                {/* Credentials Login (Email or Username) */}
                <button
                  onClick={() => setLoginMode('credentials')}
                  className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-muted hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all group"
                  data-testid="credentials-login-option"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <KeyRound className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-semibold">{t('credentials')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('email_or_username')}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                </button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-blue-500" />
                  {isRegisterMode 
                    ? (t('create_account'))
                    : (t('login'))
                  }
                </DialogTitle>
                <DialogDescription>
                  {isRegisterMode
                    ? (t('create_spenspot_account'))
                    : (t('enter_email_or_username'))
                  }
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {isRegisterMode ? (
                  // Registration form - Email required
                  <>
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        {t('name')} *
                      </label>
                      <Input
                        placeholder={t('your_name')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="register-name"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Email *
                      </label>
                      <Input
                        type="email"
                        placeholder="email@exemple.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="register-email"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('required_password_recovery')}
                      </p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        {t('password')} *
                      </label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
                        data-testid="register-password"
                      />
                      {/* Password strength indicator */}
                      {password && (
                        <div className="mt-2 space-y-1">
                          <div className="flex gap-1">
                            <div className={`h-1 flex-1 rounded ${password.length >= 8 ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <div className={`h-1 flex-1 rounded ${/[A-Z]/.test(password) ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <div className={`h-1 flex-1 rounded ${/[a-z]/.test(password) ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <div className={`h-1 flex-1 rounded ${/[0-9]/.test(password) ? 'bg-green-500' : 'bg-gray-300'}`} />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {t('password_requirements')}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  // Login form - Email OR Username
                  <>
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        {t('email_or_username')}
                      </label>
                      <Input
                        placeholder={t('email_or_username_placeholder')}
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        data-testid="login-identifier"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        {t('password')}
                      </label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                        data-testid="login-password"
                      />
                      <a 
                        href="/forgot-password"
                        className="text-xs text-blue-500 hover:underline mt-1 inline-block"
                        data-testid="forgot-password-link"
                      >
                        {t('forgot_password')}
                      </a>
                    </div>
                  </>
                )}

                <button
                  onClick={() => setIsRegisterMode(!isRegisterMode)}
                  className="text-sm text-blue-500 hover:underline"
                >
                  {isRegisterMode
                    ? (t('already_have_account'))
                    : (t('no_account_signup'))
                  }
                </button>
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={resetToSelect} className="w-full sm:w-auto">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('back')}
                </Button>
                <Button 
                  onClick={isRegisterMode ? handleRegister : handleLogin}
                  disabled={isLoggingIn || (isRegisterMode ? (!email || !password || !name) : (!identifier || !password))}
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-indigo-500"
                  data-testid="login-submit"
                >
                  {isLoggingIn ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    isRegisterMode 
                      ? (t('signup'))
                      : (t('log_in'))
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
